import { ActionContext, ActionTree } from "vuex";
import { StatusCodes } from "http-status-codes";
import {
  ICoreErrorResponse,
  ICoreResponse,
} from "@/core/interfaces/coreResponse.interface";
import { ICorporatePolicy } from "@/core/interfaces/corporatePolicy.interface";
import CorporatePolicyService from "../services/corporatePolicy.service";
import { ICorporatePolicyState, IPolicyOptionItem } from "../types";
import { StoreState } from "@/store/type";
import analyticsService from "@/analytic.service";
import { CORPORATE_POLICY_EVENTS } from "../constants/analyticsEvents";
import notificationService from "@/core/services/notification.service";
import { NOTIFICATION_TYPES } from "@/core/enums/NOTIFICATION_TYPES";
import { NOTIFICATION_MESSAGES } from "@/core/constants/notificationMessages";
import {
  CreateCorporatePolicyDto,
  CreateCorporatePolicyResponseDto,
  CreateCorporatePolicyRuleDto,
  UpdateCorporatePolicyDto,
} from "../dtos/corporatePolicy.dtos";
import type {
  IAgentDetails,
  IAgentV2,
} from "@/core/interfaces/agent.interface";
import {
  ASSIGNMENT_OPTION_SOURCE_KEYS,
  isOrganizationGroupOptionSource,
  mapOrganizationGroupsToOptions,
  mapOrganizationUsersToOptions,
} from "../utils/policyAssignmentOptionProviders";
import { normalizeOptionItems } from "../utils/optionSourceResolver";
import { formatString } from "@/core/utils/helpers";

const corporatePolicyService = new CorporatePolicyService();

export type FetchPolicyOptionSourceOptionsResult = IPolicyOptionItem[] | null;

const actions: ActionTree<ICorporatePolicyState, StoreState> = {
  async fetchPolicyBuilderConfig(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    payload: { organizationId: string },
  ) {
    try {
      context.commit("setFetchingPolicyBuilderConfig", true);

      const response = await corporatePolicyService.getBuilderConfig(
        payload.organizationId,
      );

      if (
        response.success &&
        response.status === StatusCodes.OK &&
        response.data
      ) {
        context.commit("setPolicyBuilderConfig", response.data);
      }
    } catch {
    } finally {
      context.commit("setFetchingPolicyBuilderConfig", false);
    }
  },

  async fetchCorporatePolicies(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    payload: { organizationId: string },
  ) {
    try {
      context.commit("setFetchingCorporatePolicies", true);
      const response: ICoreResponse =
        await corporatePolicyService.getCorporatePolicies(
          payload.organizationId,
        );

      if (response.success && response.status === StatusCodes.OK) {
        context.commit(
          "setCorporatePolicies",
          response.data as ICorporatePolicy[],
        );
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.CORPORATE_POLICY_FETCH,
          { organizationId: payload.organizationId },
        );
      } else {
        throw response;
      }
    } catch {
      context.commit("setCorporatePolicies", []);
    } finally {
      context.commit("setFetchingCorporatePolicies", false);
    }
  },

  async fetchMyPolicies(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    payload: { organizationUserId: string },
  ): Promise<ICorporatePolicy[]> {
    try {
      context.commit("setFetchingCorporatePolicies", true);
      const response: ICoreResponse =
        await corporatePolicyService.getCorporatePoliciesByOrganizationUser(
          payload.organizationUserId,
        );

      if (response.success && response.status === StatusCodes.OK) {
        const policies = Array.isArray(response.data)
          ? (response.data as ICorporatePolicy[])
          : response.data
            ? [response.data as ICorporatePolicy]
            : [];

        context.commit("setCorporatePolicies", policies);
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.MY_POLICIES_FETCH,
          {
            organizationUserId: payload.organizationUserId,
          },
        );
        return policies;
      } else {
        throw response;
      }
    } catch {
      context.commit("setCorporatePolicies", []);
      return [];
    } finally {
      context.commit("setFetchingCorporatePolicies", false);
    }
  },

  async fetchCorporatePolicyById(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    payload: { corporatePolicyId: string },
  ) {
    try {
      context.commit("setFetchingCorporatePolicy", true);
      const response: ICoreResponse =
        await corporatePolicyService.getCorporatePolicyById(
          payload.corporatePolicyId,
        );

      if (response.success && response.status === StatusCodes.OK) {
        context.commit(
          "setSelectedCorporatePolicy",
          response.data as ICorporatePolicy,
        );
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.CORPORATE_POLICY_FETCH_BY_ID,
          { corporatePolicyId: payload.corporatePolicyId },
        );
      } else {
        throw response;
      }
    } catch {
      context.commit("setSelectedCorporatePolicy", null);
    } finally {
      context.commit("setFetchingCorporatePolicy", false);
    }
  },

  clearSelectedCorporatePolicy(
    context: ActionContext<ICorporatePolicyState, StoreState>,
  ) {
    context.commit("setSelectedCorporatePolicy", null);
  },

  async createCorporatePolicy(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    {
      organizationId,
      body,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      body: CreateCorporatePolicyDto;
      successHandler: (publicId: string) => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ) {
    try {
      context.commit("setCorporatePolicyActionLoading", true);
      const response: ICoreResponse =
        await corporatePolicyService.createCorporatePolicy(
          organizationId,
          body,
        );

      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.CREATED)
      ) {
        const publicId = (response.data as CreateCorporatePolicyResponseDto)
          .public_id;
        successHandler(publicId);
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.CORPORATE_POLICY_CREATE,
          {
            organizationId,
            name: body.name,
            public_id: publicId,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.CORPORATE_POLICY_CREATE_SUCCESS;
      } else {
        throw response;
      }
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      if (errorHandler) {
        errorHandler(exception);
      }
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      context.commit("setCorporatePolicyActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async updateCorporatePolicy(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    {
      organizationId,
      corporatePolicyId,
      body,
      errorHandler,
    }: {
      organizationId: string;
      corporatePolicyId: string;
      body: UpdateCorporatePolicyDto;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ): Promise<boolean> {
    try {
      context.commit("setCorporatePolicyActionLoading", true);
      const response: ICoreResponse =
        await corporatePolicyService.updateCorporatePolicy(
          organizationId,
          corporatePolicyId,
          body,
        );

      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.CREATED)
      ) {
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.CORPORATE_POLICY_UPDATE,
          {
            organizationId,
            corporatePolicyId,
            name: body.name,
            public_id: body.public_id,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.CORPORATE_POLICY_UPDATE_SUCCESS;

        await context.dispatch("fetchCorporatePolicyById", {
          corporatePolicyId,
        });

        return true;
      }

      throw response;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      return false;
    } finally {
      context.commit("setCorporatePolicyActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async deleteCorporatePolicy(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    {
      organizationId,
      corporatePolicyId,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      corporatePolicyId: string;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ): Promise<boolean> {
    try {
      context.commit("setCorporatePolicyActionLoading", true);
      const response: ICoreResponse =
        await corporatePolicyService.deleteCorporatePolicy(
          organizationId,
          corporatePolicyId,
        );

      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.NO_CONTENT)
      ) {
        context.commit(
          "setCorporatePolicies",
          context.state.corporatePolicies.filter(
            (policy) => policy.public_id !== corporatePolicyId,
          ),
        );
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.CORPORATE_POLICY_DELETE,
          { organizationId, corporatePolicyId },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.CORPORATE_POLICY_DELETE_SUCCESS;
        successHandler?.();
        return true;
      }

      throw response;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      return false;
    } finally {
      context.commit("setCorporatePolicyActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async patchCorporatePolicyStatus(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    {
      organizationId,
      corporatePolicyId,
      policyName,
      isActive,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      corporatePolicyId: string;
      policyName: string;
      isActive: boolean;
      successHandler?: () => void;
      errorHandler?: () => void;
    },
  ) {
    try {
      const response: ICoreResponse =
        await corporatePolicyService.patchCorporatePolicyStatus(
          organizationId,
          corporatePolicyId,
          isActive,
        );

      if (response.success && response.status === StatusCodes.OK) {
        const updatedPolicies = context.state.corporatePolicies.map(
          (policy) => {
            if (policy.public_id === corporatePolicyId) {
              return { ...policy, is_active: isActive };
            }
            return policy;
          },
        );
        context.commit("setCorporatePolicies", updatedPolicies);

        if (
          context.state.selectedCorporatePolicy?.public_id === corporatePolicyId
        ) {
          context.commit("setSelectedCorporatePolicy", {
            ...context.state.selectedCorporatePolicy,
            is_active: isActive,
          });
        }

        successHandler?.();
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.CORPORATE_POLICY_STATUS_TOGGLE,
          { organizationId, corporatePolicyId, policyName, isActive },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description = formatString(
          NOTIFICATION_MESSAGES.CORPORATE_POLICY_STATUS_UPDATE_SUCCESS,
          policyName,
          isActive ? "active" : "inactive",
        );
      } else {
        throw response;
      }
    } catch (error: unknown) {
      errorHandler?.();
      const exception = error as ICoreErrorResponse;
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
    } finally {
      notificationService.triggerNotification();
    }
  },

  async createCorporatePolicyRules(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    {
      corporatePolicyId,
      body,
      successHandler,
      errorHandler,
    }: {
      corporatePolicyId: string;
      body: CreateCorporatePolicyRuleDto;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ): Promise<boolean> {
    try {
      context.commit("setCorporatePolicyActionLoading", true);
      const response: ICoreResponse =
        await corporatePolicyService.createCorporatePolicyRules(
          corporatePolicyId,
          body,
        );

      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.CREATED)
      ) {
        successHandler?.();
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.CORPORATE_POLICY_RULES_CREATE,
          {
            corporatePolicyId,
            rule_category: body.rule_category,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.CORPORATE_POLICY_RULES_CREATE_SUCCESS;

        await context.dispatch("fetchCorporatePolicyById", {
          corporatePolicyId,
        });

        return true;
      }

      throw response;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      return false;
    } finally {
      context.commit("setCorporatePolicyActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async updateCorporatePolicyRules(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    {
      corporatePolicyId,
      rulePublicId,
      body,
      successHandler,
      errorHandler,
    }: {
      corporatePolicyId: string;
      rulePublicId: string;
      body: CreateCorporatePolicyRuleDto;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ): Promise<boolean> {
    try {
      context.commit("setCorporatePolicyActionLoading", true);
      const response: ICoreResponse =
        await corporatePolicyService.updateCorporatePolicyRules(
          rulePublicId,
          body,
        );

      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.CREATED)
      ) {
        successHandler?.();
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.CORPORATE_POLICY_RULES_UPDATE,
          {
            corporatePolicyId,
            rulePublicId,
            rule_category: body.rule_category,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          NOTIFICATION_MESSAGES.CORPORATE_POLICY_RULES_UPDATE_SUCCESS;

        await context.dispatch("fetchCorporatePolicyById", {
          corporatePolicyId,
        });

        return true;
      }

      throw response;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      return false;
    } finally {
      context.commit("setCorporatePolicyActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async deleteCorporatePolicyRules(
    context: ActionContext<ICorporatePolicyState, StoreState>,
    {
      organizationId,
      corporatePolicyId,
      rulePublicId,
      successHandler,
      errorHandler,
    }: {
      organizationId: string;
      corporatePolicyId: string;
      rulePublicId: string;
      successHandler?: () => void;
      errorHandler?: (error: ICoreErrorResponse) => void;
    },
  ): Promise<boolean> {
    try {
      context.commit("setCorporatePolicyActionLoading", true);
      const response: ICoreResponse =
        await corporatePolicyService.deleteCorporatePolicyRules(
          organizationId,
          corporatePolicyId,
          rulePublicId,
        );

      if (
        response.success &&
        (response.status === StatusCodes.OK ||
          response.status === StatusCodes.NO_CONTENT)
      ) {
        successHandler?.();
        analyticsService.logActionEvent(
          CORPORATE_POLICY_EVENTS.CORPORATE_POLICY_RULES_DELETE,
          {
            organizationId,
            corporatePolicyId,
            rulePublicId,
          },
        );
        notificationService.type = NOTIFICATION_TYPES.SUCCESS;
        notificationService.description =
          response.message ||
          NOTIFICATION_MESSAGES.CORPORATE_POLICY_RULES_DELETE_SUCCESS;

        await context.dispatch("fetchCorporatePolicyById", {
          corporatePolicyId,
        });

        return true;
      }

      throw response;
    } catch (error: unknown) {
      const exception = error as ICoreErrorResponse;
      errorHandler?.(exception);
      notificationService.type = NOTIFICATION_TYPES.ERROR;
      notificationService.description =
        exception.message || exception.error || NOTIFICATION_MESSAGES.DEFAULT;
      return false;
    } finally {
      context.commit("setCorporatePolicyActionLoading", false);
      notificationService.triggerNotification();
    }
  },

  async fetchPolicyOptionSourceOptions(
    _context: ActionContext<ICorporatePolicyState, StoreState>,
    {
      url,
      params,
      labelField,
      valueField,
      optionSourceKey,
      signal,
    }: {
      url: string;
      params?: Record<string, unknown>;
      labelField: string;
      valueField: string;
      optionSourceKey: string;
      signal?: AbortSignal;
    },
  ): Promise<FetchPolicyOptionSourceOptionsResult> {
    const response = await corporatePolicyService.getOptionSourceOptions(
      url,
      params,
      { signal },
    );

    if (response.aborted) {
      return null;
    }

    if (!response.success) {
      return [];
    }

    if (optionSourceKey === ASSIGNMENT_OPTION_SOURCE_KEYS.USERS) {
      const data = response.data as {
        users?: Array<IAgentV2 | IAgentDetails>;
      };
      return mapOrganizationUsersToOptions(data.users ?? []);
    }

    if (isOrganizationGroupOptionSource(optionSourceKey)) {
      return mapOrganizationGroupsToOptions(response.data, {
        labelField,
        valueField,
      });
    }

    return normalizeOptionItems(response.data, { labelField, valueField });
  },
};

export default actions;
